import styled, { css } from 'styled-components'

import { media } from '../../../utils/mediaQuery'

export const LayersPanelContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<{
  $active: boolean
  $position?: {
    top?: string
    right?: string
    bottom?: string
    left?: string
  }
  $width?: string
  $zIndex?: number
}>`
  position: absolute;
  ${({ $position }) =>
    $position
      ? css`
          ${$position.top && `top: ${$position.top};`}
          ${$position.right && `right: ${$position.right};`}
          ${$position.bottom && `bottom: ${$position.bottom};`}
          ${$position.left && `left: ${$position.left};`}
        `
      : css`
          top: 0;
          right: 0;
          bottom: 0;
        `}
  width: ${({ $width }) => $width || '340px'};
  max-height: 100vh;
  height: 100%;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: 12px 0 0 12px;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  transform: translateX(${({ $active }) => ($active ? '0' : '100%')});
  transition:
    transform 0.3s ease-in-out,
    visibility 0s linear ${({ $active }) => ($active ? '0s' : '0.3s')};
  z-index: ${({ $zIndex }) => $zIndex || 1000};
  display: flex;
  flex-direction: column;
  visibility: ${({ $active }) => ($active ? 'visible' : 'hidden')};
  pointer-events: ${({ $active }) => ($active ? 'auto' : 'none')};

  ${media.tabletDown} {
    width: 80vw;
    max-width: 320px;
  }

  @media (max-width: 480px) {
    width: 90vw;
  }
`

export const LayersHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
`

export const LayersTitle = styled.h3`
  font-size: 16px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`

export const ActiveLayersBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background: ${({ theme }) => theme.colors.primary};
  color: #ffffff;
  font-size: 11px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  border-radius: 10px;
  line-height: 1;
`

export const LayersClose = styled.button`
  background: none;
  border: none;
  padding: 6px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`

export const LayersContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 20px;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.background.secondary};
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border.default};
    border-radius: 2px;
  }
`

export const SearchContainer = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
`

export const SearchInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`

export const SearchInput = styled.input`
  width: 100%;
  padding: 8px 10px 8px 32px;
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.background.secondary};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: 8px;
  outline: none;
  transition: all 0.2s;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.background.primary};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.tertiary};
  }
`

export const SearchIcon = styled.div`
  position: absolute;
  left: 12px;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  pointer-events: none;
`

export const CollapsibleGroupHeader = styled.div<{ $expanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding: 4px 0;
  user-select: none;
  transition: all 0.2s;

  &:hover {
    opacity: 0.8;
  }
`

export const GroupTitleWithIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

export const GroupContent = styled.div<{ $expanded: boolean }>`
  max-height: ${({ $expanded }) => ($expanded ? '2000px' : '0')};
  opacity: ${({ $expanded }) => ($expanded ? '1' : '0')};
  overflow: ${({ $expanded }) => ($expanded ? 'visible' : 'hidden')};
  pointer-events: ${({ $expanded }) => ($expanded ? 'auto' : 'none')};
  transition:
    max-height 0.3s ease-in-out,
    opacity 0.3s ease-in-out,
    overflow 0s ${({ $expanded }) => ($expanded ? '0.3s' : '0s')};
`

export const LayerGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

export const LayerGroupTitle = styled.h4`
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 4px 0;
`

export const LayerItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
`

export const LayerLabel = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  user-select: none;
  flex: 1;
`

export const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 4px 0;
`

export const RadioOption = styled.label<{ $checked: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${({ $checked, theme }) =>
    $checked ? theme.colors.background.secondary : 'transparent'};
  border: 1px solid
    ${({ $checked, theme }) =>
      $checked ? theme.colors.primary : theme.colors.border.light};

  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
  }
`

export const RadioButton = styled.div<{ $checked: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid
    ${({ $checked, theme }) =>
      $checked ? theme.colors.primary : theme.colors.border.default};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s;

  &::after {
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.primary};
    opacity: ${({ $checked }) => ($checked ? '1' : '0')};
    transform: scale(${({ $checked }) => ($checked ? '1' : '0')});
    transition: all 0.2s;
  }
`

export const RadioLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
`

export const RadioTitle = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`

export const RadioDescription = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  color: ${({ theme }) => theme.colors.text.secondary};
`

// Card-based design for layer groups
export const LayerGroupCard = styled.div.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<{ $groupType?: 'base' | 'dynamic'; $expanded?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $expanded }) => ($expanded ? '12px' : '0')};
  padding: 12px;
  border-radius: 8px;
  transition: all 0.2s;

  ${({ $groupType, theme }) =>
    $groupType === 'base'
      ? css`
          background: ${theme.colors.background.secondary}80;
          border: 1px solid ${theme.colors.border.light};
        `
      : $groupType === 'dynamic'
        ? css`
            background: ${theme.colors.primary}08;
            border: 2px solid ${theme.colors.primary}33;
          `
        : css`
            background: transparent;
            border: 1px solid ${theme.colors.border.light};
          `}
`

export const LayerGroupHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

export const LayerGroupHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`

export const LayerGroupIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 16px;
`

export const LayerGroupTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`

export const SectionBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  background: ${({ theme }) => theme.colors.primary};
  color: #ffffff;
  font-size: 11px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  border-radius: 10px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`

export const QuickActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
`

export const QuickActionButton = styled.button`
  padding: 4px 10px;
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.primary};
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.primary}66;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.primary}15;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:active {
    transform: scale(0.98);
  }
`

export const OpacityControl = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0 4px 0;
  margin-top: 4px;
`

export const OpacityLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.tertiary};
  min-width: 60px;
`

export const OpacitySlider = styled.input`
  flex: 1;
  height: 4px;
  border-radius: 2px;
  outline: none;
  -webkit-appearance: none;
  appearance: none;
  background: ${({ theme }) => theme.colors.border};

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.primary};
    cursor: pointer;
    border: 2px solid ${({ theme }) => theme.colors.background.primary};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }

  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.primary};
    cursor: pointer;
    border: 2px solid ${({ theme }) => theme.colors.background.primary};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }

  &:hover::-webkit-slider-thumb {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  }

  &:hover::-moz-range-thumb {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  }
`

export const OpacityValue = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  min-width: 32px;
  text-align: right;
  font-variant-numeric: tabular-nums;
`

export const StyledSelect = styled.select`
  width: 100%;
  padding: 12px 36px 12px 16px;
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  background-color: ${({ theme }) => theme.colors.background.primary};
  border: 2px solid ${({ theme }) => theme.colors.border.light};
  border-radius: 10px;
  cursor: pointer;
  outline: none;
  transition: all 0.2s;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%234A5568' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  background-size: 16px;
  line-height: 1.5;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary}88;
    background-color: ${({ theme }) => theme.colors.background.secondary}40;
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 4px ${({ theme }) => theme.colors.primary}20;
    background-color: ${({ theme }) => theme.colors.background.primary};
  }

  option {
    padding: 12px 16px;
    background-color: ${({ theme }) => theme.colors.background.primary};
    color: ${({ theme }) => theme.colors.text.primary};
    font-size: ${({ theme }) => theme.typography.fontSize.body};
    line-height: 1.6;
    min-height: 44px;

    &:hover {
      background-color: ${({ theme }) => theme.colors.primary}10;
    }

    &:checked {
      background-color: ${({ theme }) => theme.colors.primary}20;
      font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
    }
  }
`

// Custom Dropdown Components
export const CustomDropdownContainer = styled.div`
  position: relative;
  width: 100%;
`

export const CustomDropdownTrigger = styled.button<{ $isOpen: boolean }>`
  width: 100%;
  padding: 12px 40px 12px 16px;
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  background-color: ${({ theme }) => theme.colors.background.primary};
  border: 2px solid
    ${({ theme, $isOpen }) =>
      $isOpen ? theme.colors.primary : theme.colors.border.light};
  border-radius: 10px;
  cursor: pointer;
  outline: none;
  transition: all 0.2s;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
  line-height: 1.5;
  box-shadow: ${({ $isOpen, theme }) =>
    $isOpen ? `0 0 0 4px ${theme.colors.primary}20` : 'none'};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary}88;
    background-color: ${({ theme }) => theme.colors.background.secondary}40;
  }
`

export const CustomDropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background-color: ${({ theme }) => theme.colors.background.primary};
  border: 2px solid ${({ theme }) => theme.colors.border.light};
  border-radius: 10px;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  max-height: 600px;
  overflow-y: auto;
  z-index: 1000;
  padding: 4px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.background.secondary};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border.default};
    border-radius: 3px;
  }
`

export const CustomDropdownOption = styled.div<{
  $isSelected: boolean
  $isDisabled?: boolean
}>`
  padding: 12px;
  cursor: ${({ $isDisabled }) => ($isDisabled ? 'not-allowed' : 'pointer')};
  border-radius: 8px;
  transition: all 0.2s;
  background-color: ${({ $isSelected, $isDisabled, theme }) =>
    $isDisabled
      ? theme.colors.background.secondary + '40'
      : $isSelected
        ? theme.colors.primary + '10'
        : 'transparent'};
  border: 2px solid
    ${({ $isSelected, $isDisabled, theme }) =>
      $isDisabled
        ? 'transparent'
        : $isSelected
          ? theme.colors.primary + '40'
          : 'transparent'};
  opacity: ${({ $isDisabled }) => ($isDisabled ? 0.5 : 1)};

  &:hover {
    background-color: ${({ $isDisabled, theme }) =>
      $isDisabled ? 'transparent' : theme.colors.background.secondary};
  }
`

export const OptionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 8px;
  gap: 12px;
`

export const OptionLabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`

export const OptionLabel = styled.div<{
  $isSelected: boolean
  $isDisabled?: boolean
}>`
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  font-weight: ${({ $isSelected, theme }) =>
    $isSelected
      ? theme.typography.fontWeight.semibold
      : theme.typography.fontWeight.medium};
  color: ${({ $isDisabled, theme }) =>
    $isDisabled ? theme.colors.text.tertiary : theme.colors.text.primary};
  line-height: 1.4;
`

export const OptionDescription = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.5;
  font-style: italic;
`

export const CheckIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary};
  flex-shrink: 0;
  margin-top: 2px;
`

export const OptionLegend = styled.div`
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
`

export const LayerDescription = styled.div`
  padding: 8px 0 0 0;
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.5;
  font-style: italic;
`
